import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card } from "react-bootstrap";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from "recharts";
import { getAllEmployees, getDailyReports } from "../../../../api/hrDashboard";

// --- Professional Card Wrapper ---
const ChartCard = ({ title, subtitle, children }) => (
  <Card style={{
    borderRadius: 12, border: "1px solid #eef2f6",
    boxShadow: "0 2px 4px rgba(0,0,0,.02)", height: "100%", overflow: "hidden"
  }}>
    <div style={{ padding: "18px 20px 2px" }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{subtitle}</div>}
    </div>
    <div style={{ padding: "12px 18px 20px" }}>{children}</div>
  </Card>
);

const ChartsSection = () => {
  const [employees, setEmployees] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const COLORS = ["#4f6ef7", "#4ade80", "#818cf8", "#f472b6", "#fb923c", "#94a3b8"];

  // Fetch real-time data
  useEffect(() => {
    Promise.all([getAllEmployees(), getDailyReports()])
      .then(([empRes, repRes]) => {
        setEmployees(empRes.data || []);
        setReports(repRes.data || []);
      })
      .catch((err) => console.error("Error fetching HR data:", err))
      .finally(() => setLoading(false));
  }, []);

  // --- Logic 1: Employee Distribution (Donut) ---
  const distributionData = useMemo(() => {
    const deptMap = {};
    employees.filter(e => !e.deboardEmp).forEach(e => {
      const d = e.departmentName || "Unassigned";
      deptMap[d] = (deptMap[d] || 0) + 1;
    });
    return Object.entries(deptMap).map(([name, value]) => ({ name, value }));
  }, [employees]);

  // --- Logic 2: Attendance Trend (Line) ---
  const attendanceTrend = useMemo(() => {
    const today = new Date();
    const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const results = [];

    for (let i = 6; i >= 0; i--) {
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() - i);
      const dateStr = targetDate.toISOString().split("T")[0];
      
      const count = reports.filter(r => 
        (r.reportDate || r.date || "").startsWith(dateStr)
      ).length;

      results.push({
        day: DAY_LABELS[targetDate.getDay()],
        count: count
      });
    }
    return results;
  }, [reports]);

  if (loading) return <div style={{ padding: 20, color: "#94a3b8" }}>Syncing dashboard data...</div>;

  return (
    <div className="mt-3">
      <Row className="g-4">
        
        {/* --- Donut Chart --- */}
        <Col xl={4} lg={6} md={12}>
          <ChartCard title="Employee Distribution" subtitle="Active staff by department">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={distributionData}
                  innerRadius={65}
                  outerRadius={85}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {distributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} 
                />
                
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>

        {/* --- Line Chart --- */}
        <Col xl={4} lg={6} md={12}>
          <ChartCard title="Attendance Trend" subtitle="Daily submissions (Last 7 Days)">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={attendanceTrend} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#4f6ef7" 
                  strokeWidth={3} 
                  dot={{ r: 4, fill: "#4f6ef7", strokeWidth: 2, stroke: "#fff" }}
                  activeDot={{ r: 6 }} 
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>

        {/* --- Bar Chart --- */}
        <Col xl={4} lg={12} md={12}>
          <ChartCard title="Department Headcount" subtitle="Total strength overview">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={distributionData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <Tooltip cursor={{fill: '#f8fafc'}} contentStyle={{ borderRadius: 8, border: "none" }} />
                <Bar dataKey="value" fill="#4f6ef7" radius={[4, 4, 0, 0]} barSize={35} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>

      </Row>
    </div>
  );
};

export default ChartsSection;